import type { GlossaryTerm } from "@/types/glossary";
import { splitAliases } from "@/lib/glossaryFilter";

// Short German inflection endings we allow after a term so that "Sprint" also
// underlines "Sprints" / "Iteration" also "Iterationen", while a real compound
// like "Sprintphase" is NOT matched (its trailing "phase" is not an ending, so
// the word-end lookahead fails and the term is left untouched). This is
// intentionally stricter than glossaryFilter's presence detection, because
// inline marking must not mislink whole compound words to a base term.
const INFLECTION_SUFFIX = "(?:en|em|er|es|e|n|s)?";

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export interface CompiledGlossary {
  regex: RegExp;
  // groupTermIds[i] is the term id for capture group (i + 1).
  groupTermIds: string[];
}

/**
 * Build a single case-insensitive Unicode regex that matches any glossary term
 * or alias at a word start (allowing a short inflection suffix). Each candidate
 * gets its own capture group so a match can be mapped back to its term id.
 * Candidates are sorted longest-first so overlapping alternatives prefer the
 * longer term (e.g. "Sprint Backlog" over "Sprint") at the same position.
 */
export const compileGlossaryRegex = (
  terms: GlossaryTerm[]
): CompiledGlossary | null => {
  const candidates: { termId: string; value: string }[] = [];
  for (const term of terms) {
    for (const raw of [term.name, ...splitAliases(term.aliases)]) {
      const value = raw.trim();
      if (value.length >= 2) candidates.push({ termId: term.id, value });
    }
  }
  if (candidates.length === 0) return null;

  candidates.sort((a, b) => b.value.length - a.value.length);

  const groupTermIds = candidates.map((c) => c.termId);
  const alternation = candidates
    .map((c) => `(${escapeRegExp(c.value)})`)
    .join("|");
  const regex = new RegExp(
    `(?<![\\p{L}\\p{N}])(?:${alternation})${INFLECTION_SUFFIX}(?![\\p{L}\\p{N}])`,
    "giu"
  );
  return { regex, groupTermIds };
};

export interface GlossaryMatch {
  start: number;
  end: number;
  termId: string;
  text: string;
}

/**
 * Cross-block claim registry: ensures each term is marked at most once per
 * subchapter, even though every CourseMarkdown block runs its own rehype pass.
 * A claim is owned by the block (ownerId) that first matched the term, so a
 * re-render of that block keeps its mark while other blocks stay unmarked.
 * The registry is recreated when the subchapter changes (see
 * GlossaryHighlightProvider's resetKey).
 */
export interface GlossaryClaims {
  claim: (termId: string, ownerId: string) => boolean;
}

export const createGlossaryClaims = (): GlossaryClaims => {
  const owners = new Map<string, string>();
  return {
    claim: (termId, ownerId) => {
      const owner = owners.get(termId);
      if (owner === undefined) {
        owners.set(termId, ownerId);
        return true;
      }
      return owner === ownerId;
    },
  };
};

/**
 * Find glossary matches in a piece of text, claiming each term at most once via
 * the shared `claimed` set. Only the first, unclaimed occurrence of a term is
 * returned; later occurrences are skipped. When `canClaim` is given, a term is
 * additionally checked against the cross-block registry — if another block
 * already owns it, the term is skipped here (and remembered in `claimed` so
 * later occurrences are not re-checked). Matches never overlap. `claimed` is
 * mutated.
 */
export const findGlossaryMatches = (
  text: string,
  compiled: CompiledGlossary,
  claimed: Set<string>,
  canClaim?: (termId: string) => boolean
): GlossaryMatch[] => {
  const { regex: sharedRegex, groupTermIds } = compiled;
  // Build a fresh regex per call via matchAll. The compiled regex is shared
  // across every CourseMarkdown render and text node; reusing its `exec`
  // iteration state across calls breaks matching, so never iterate it directly.
  const regex = new RegExp(sharedRegex.source, sharedRegex.flags);
  const matches: GlossaryMatch[] = [];
  for (const match of text.matchAll(regex)) {
    if (match[0] === "" || match.index === undefined) continue;
    let termId: string | undefined;
    for (let i = 1; i < match.length; i += 1) {
      if (match[i] !== undefined) {
        termId = groupTermIds[i - 1];
        break;
      }
    }
    if (!termId || claimed.has(termId)) continue;
    claimed.add(termId);
    if (canClaim && !canClaim(termId)) continue;
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      termId,
      text: match[0],
    });
  }
  return matches;
};

// hast node shapes we care about (kept minimal to avoid a hast-types dep).
interface HastText {
  type: "text";
  value: string;
}
interface HastElement {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}
type HastNode =
  | HastText
  | HastElement
  | { type: string; children?: HastNode[] };

// Marks a span produced by this plugin; CourseMarkdown's `span` override reads
// this to render the interactive GlossaryTermMark.
export const GLOSSARY_TERM_PROP = "dataGlossaryTermId";

// Elements whose text must not be turned into glossary links.
const SKIP_ELEMENTS = new Set(["a", "code", "pre"]);

export interface RehypeGlossaryOptions {
  compiled: CompiledGlossary | null;
  // Cross-block registry + the id of the markdown block running this plugin.
  // When present, "first occurrence" is scoped to the whole subchapter instead
  // of a single markdown block.
  claims?: GlossaryClaims;
  ownerId?: string;
}

/**
 * rehype plugin: wraps the first occurrence of each glossary term in a
 * `<span data-glossary-term-id="…">`, which CourseMarkdown maps to the
 * interactive GlossaryTermMark component.
 *
 * `claimed` is created fresh inside the transformer on every run, keeping the
 * plugin idempotent per block: react-markdown re-runs it on each render, so a
 * set captured from options would accumulate across renders and stop matching.
 * Cross-block state deliberately lives in `claims` instead — persisting across
 * renders is its purpose, and idempotency is preserved because claims are
 * owner-scoped (re-claiming your own term succeeds).
 */
export const rehypeGlossary =
  ({ compiled, claims, ownerId }: RehypeGlossaryOptions) =>
  (tree: HastNode): void => {
    if (!compiled) return;
    const claimed = new Set<string>();
    const canClaim =
      claims && ownerId
        ? (termId: string) => claims.claim(termId, ownerId)
        : undefined;

    const walk = (node: HastNode): void => {
      const children = (node as HastElement).children;
      if (!children) return;

      const next: HastNode[] = [];
      for (const child of children) {
        if (child.type === "element") {
          const el = child as HastElement;
          if (!SKIP_ELEMENTS.has(el.tagName)) walk(el);
          next.push(el);
          continue;
        }
        if (child.type !== "text") {
          next.push(child);
          continue;
        }

        const value = (child as HastText).value;
        const found = findGlossaryMatches(value, compiled, claimed, canClaim);
        if (found.length === 0) {
          next.push(child);
          continue;
        }

        let cursor = 0;
        for (const m of found) {
          if (m.start > cursor) {
            next.push({ type: "text", value: value.slice(cursor, m.start) });
          }
          next.push({
            type: "element",
            tagName: "span",
            properties: { [GLOSSARY_TERM_PROP]: m.termId },
            children: [{ type: "text", value: value.slice(m.start, m.end) }],
          });
          cursor = m.end;
        }
        if (cursor < value.length) {
          next.push({ type: "text", value: value.slice(cursor) });
        }
      }
      children.splice(0, children.length, ...next);
    };

    walk(tree);
  };
